import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/api/endpoints';
import { ApiClientError } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/*
  Changing a password REVOKES EVERY refresh-token family server-side, including
  this device's. The access token stays valid until it expires, so the UI must
  say so plainly — a user who is silently signed out of their phone an hour
  later reads that as a bug, or worse, as someone else in their account.
*/
export function SecurityCard() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;

  const mutation = useMutation({
    mutationFn: () => authApi.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    },
  });

  const reset = () => {
    setOpen(false);
    setDone(false);
    setCurrent('');
    setNext('');
    setConfirm('');
    mutation.reset();
  };

  const blocked = !current || next.length < 8 || next !== confirm;

  return (
    <Card title="Security">
      {!open ? (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-white/55">
            Change your password. Doing so signs you out on every other device.
          </p>
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            Change
          </Button>
        </div>
      ) : done ? (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-brand-green">
            Password changed. Other devices have been signed out.
          </p>
          <Button variant="secondary" size="sm" onClick={reset}>
            Close
          </Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!blocked) mutation.mutate();
          }}
        >
          <Input
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            hint="At least 8 characters."
            error={tooShort ? 'Must be at least 8 characters.' : undefined}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <Input
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            error={mismatch ? 'Passwords do not match.' : undefined}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />

          {mutation.error && (
            <p className="text-sm text-red-400">
              {mutation.error instanceof ApiClientError
                ? mutation.error.error.message
                : 'Could not change your password. Please try again.'}
            </p>
          )}

          <div className="flex gap-2">
            <Button type="submit" size="sm" loading={mutation.isPending} disabled={blocked}>
              Save password
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={reset} disabled={mutation.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
